/* Invoice → Consignee by method: settings (Emirates fixed, DHL / FedEx / UPS … typed per invoice) + per-invoice fill dialog. */
import { useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { toast } from "sonner";
import { Plus, Settings2, Trash2, X } from "lucide-react";

const LABELS = [["consignee", "Consignee (name + address, one per line)"], ["final_destination", "Final Destination"],
  ["country_destination", "Country of Final Destination"], ["port_discharge", "Port of Discharge"], ["articles", "No. of Articles"]];
const err = (e) => toast.error(e.response?.data?.detail || "Failed");
const inp = "w-full rounded-md border px-2 py-1 text-xs";

const Fields = ({ v, set, tid }) => (
  <div className="grid gap-2 sm:grid-cols-2">
    {LABELS.map(([k, l]) => (
      <label key={k} className={`text-[11px] text-slate-600 ${k === "consignee" ? "sm:col-span-2" : ""}`}>{l}
        {k === "consignee"
          ? <textarea rows={4} value={v[k] || ""} onChange={(e) => set({ ...v, [k]: e.target.value })} className={inp} data-testid={`${tid}-${k}`} />
          : <input value={v[k] || ""} onChange={(e) => set({ ...v, [k]: e.target.value })} placeholder="blank = AS PER PACKING LIST" className={inp} data-testid={`${tid}-${k}`} />}
      </label>))}
  </div>
);

export const ConsigneeRules = () => {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState([]);
  useEffect(() => { if (open) axios.get(`${API}/invoice-doc/consignee-rules`, { headers: getAuthHeader() }).then((r) => setRules(r.data.rules)).catch(err); }, [open]);
  const upd = (i, x) => setRules((rs) => rs.map((r, j) => (j === i ? x : r)));
  const save = async () => {
    try { const r = await axios.put(`${API}/invoice-doc/consignee-rules`, { rules }, { headers: getAuthHeader() }); setRules(r.data.rules); toast.success("Consignee settings saved — applies to new / rebuilt invoices"); }
    catch (e) { err(e); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-slate-50" data-testid="inv-cons-settings-btn"><Settings2 className="h-3.5 w-3.5" /> Consignee by method</button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" data-testid="inv-cons-settings">
          <div className="w-full max-w-3xl space-y-3 rounded-xl bg-white p-4">
            <div className="flex items-center justify-between"><b>Consignee by shipping method</b><button onClick={() => setOpen(false)} data-testid="inv-cons-settings-close"><X className="h-4 w-4" /></button></div>
            <p className="text-xs text-slate-500"><b>Fixed</b> = same on every invoice of that method (e.g. Emirates). <b>Ask each time</b> = the invoice shows "Fill consignee" and you type them per invoice. Methods not listed keep "AS PER PACKING LIST".</p>
            {rules.map((r, i) => (
              <div key={i} className="space-y-2 rounded-lg border p-3" data-testid={`inv-cons-rule-${i}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <input value={r.match} onChange={(e) => upd(i, { ...r, match: e.target.value.toUpperCase() })} placeholder="Method word e.g. DHL" className="w-40 rounded-md border px-2 py-1 text-xs font-semibold" data-testid={`inv-cons-match-${i}`} />
                  <select value={r.mode} onChange={(e) => upd(i, { ...r, mode: e.target.value })} className="rounded-md border px-2 py-1 text-xs" data-testid={`inv-cons-mode-${i}`}>
                    <option value="fixed">Fixed</option><option value="ask">Ask each time</option></select>
                  <button onClick={() => setRules((rs) => rs.filter((_, j) => j !== i))} className="ml-auto text-rose-600" data-testid={`inv-cons-del-${i}`}><Trash2 className="h-4 w-4" /></button>
                </div>
                {r.mode === "fixed" ? <Fields v={r} set={(x) => upd(i, x)} tid={`inv-cons-${i}`} /> : <p className="text-[11px] text-slate-500">Typed on each invoice (last used ones offered to pick).</p>}
              </div>))}
            <div className="flex gap-2">
              <button onClick={() => setRules((rs) => [...rs, { match: "", mode: "ask" }])} className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs" data-testid="inv-cons-add"><Plus className="h-3.5 w-3.5" /> Add method</button>
              <button onClick={save} className="ml-auto rounded-md bg-slate-900 px-4 py-1.5 text-xs font-semibold text-white" data-testid="inv-cons-save">Save</button>
            </div>
          </div>
        </div>)}
    </>
  );
};

export const ConsigneeCell = ({ r, onDone }) => {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState({});
  const [mem, setMem] = useState([]);
  const [busy, setBusy] = useState(false);
  if (!r.consignee_needed && !r.consignee_info) return null;
  const start = () => {
    setV(r.consignee_info || {}); setOpen(true);
    axios.get(`${API}/invoice-doc/consignee-memory`, { params: { method: r.method }, headers: getAuthHeader() }).then((x) => setMem(x.data.items)).catch(() => {});
  };
  const save = async () => {
    setBusy(true);
    try { await axios.put(`${API}/invoice-doc/history/${r.id}/consignee`, v, { headers: getAuthHeader() }); toast.success("Consignee saved — invoice rebuilt"); setOpen(false); onDone(); }
    catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={start} className={`mt-1 block rounded px-1.5 py-0.5 text-[10px] font-semibold ${r.consignee_needed ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`} data-testid={`inv-cons-btn-${r.invoice_no}`}>
        {r.consignee_needed ? "Fill consignee" : "Edit consignee"}</button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" data-testid="inv-cons-dialog">
          <div className="w-full max-w-xl space-y-3 rounded-xl bg-white p-4 text-left">
            <div className="flex items-center justify-between"><b>Consignee — {r.invoice_no} ({r.method})</b><button onClick={() => setOpen(false)} data-testid="inv-cons-dialog-close"><X className="h-4 w-4" /></button></div>
            {mem.length > 0 && <div className="flex flex-wrap gap-1">{mem.map((m, i) => (
              <button key={i} onClick={() => setV(m)} className="rounded-full border px-2 py-0.5 text-[11px] hover:bg-indigo-50" title={m.consignee} data-testid={`inv-cons-mem-${i}`}>{(m.consignee || "").split("\n")[0]}{m.final_destination ? ` · ${m.final_destination}` : ""}</button>))}</div>}
            <Fields v={v} set={setV} tid="inv-cons-f" />
            <div className="flex justify-end"><button onClick={save} disabled={busy} className="rounded-md bg-slate-900 px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-50" data-testid="inv-cons-dialog-save">Save &amp; rebuild invoice</button></div>
          </div>
        </div>)}
    </>
  );
};
